import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Swal2 from 'sweetalert2';
import { RequestService } from 'src/app/services/request/request.service';
import { SocialInteractionNotificationService } from 'src/app/services/notifications/social-interaction-notification.service';

@Component({
  selector: 'app-profile-game',
  templateUrl: './profile-game.component.html',
  styleUrls: ['./profile-game.component.scss'],
})
export class ProfileGameComponent implements OnInit, OnDestroy {
  @Input() gameType: 'race' | 'rps';
  @Input() opponentProfile: any;
  @Input() currentProfile: any;

  activeGame: any = null;
  loading = false;
  raceLocalTaps = 0;
  raceSecondsLeft = 30;
  selectedRpsChoice: 'rock' | 'paper' | 'scissors' | null = null;
  private pollTimer?: ReturnType<typeof setInterval>;
  private flushTimer?: ReturnType<typeof setInterval>;
  private unsentTaps = 0;

  constructor(
    private modalController: ModalController,
    private request: RequestService,
    private notifications: SocialInteractionNotificationService,
  ) {}

  ngOnInit(): void {}

  createChallenge(): void {
    if (this.loading) return;
    this.loading = true;
    this.request.createRequestPost('profile-games', {
      opponent_profile_id: this.opponentProfile?.id,
      game_type: this.gameType,
    }).then((raw: any) => {
      this.activeGame = this.parse(raw);
      this.beginPolling();
      return this.notifications.notifyLocal(
        'Challenge sent',
        this.gameType === 'race'
          ? 'The Tap Race invitation was sent.'
          : 'The three-round RPS invitation was sent.',
        { game_id: this.activeGame?.id, game_type: this.gameType },
      );
    }).catch((error) => {
      console.error('Unable to create profile game.', error);
      Swal2.fire({
        title: 'Challenge not sent',
        text: 'Please try again.',
        icon: 'error',
        background: '#070707',
        color: '#ffffff',
      });
    }).finally(() => this.loading = false);
  }

  tapRace(): void {
    if (this.activeGame?.status !== 'active' || this.raceSecondsLeft <= 0) return;
    this.raceLocalTaps++;
    this.unsentTaps++;
  }

  chooseRps(choice: 'rock' | 'paper' | 'scissors'): void {
    if (!this.activeGame?.id || this.activeGame?.status !== 'active' || !this.isMyTurn()) return;
    this.loading = true;
    this.selectedRpsChoice = choice;
    this.request.createRequestPost('profile-games', { choice }, { id: this.activeGame.id }, 'move')
      .then((raw: any) => this.activeGame = this.parse(raw))
      .catch((error) => {
        this.selectedRpsChoice = null;
        throw error;
      })
      .finally(() => this.loading = false);
  }

  gameScore(profileId: number): number {
    return Number(this.activeGame?.scores?.[String(profileId)] || 0);
  }

  raceTapCount(profileId: number): number {
    const serverCount = Number(this.activeGame?.tap_counts?.[String(profileId)] || 0);
    return profileId === Number(this.currentProfile?.id)
      ? Math.max(serverCount, this.raceLocalTaps)
      : serverCount;
  }

  isMyTurn(): boolean {
    return Number(this.activeGame?.current_turn_profile_id) === Number(this.currentProfile?.id);
  }

  rpsEmoji(choice: string): string {
    return choice === 'rock' ? '🪨' : choice === 'paper' ? '📄' : '✂️';
  }

  close(): void {
    this.flushTaps();
    this.modalController.dismiss();
  }

  private parse(raw: any): any {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  }

  private beginPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = setInterval(() => this.refresh(), 1500);
  }

  private refresh(): void {
    if (!this.activeGame?.id) return;
    this.request.createRequestGet('profile-games', null, { id: this.activeGame.id })
      .then((raw: any) => {
        this.activeGame = this.parse(raw);
        if (this.gameType === 'race' && this.activeGame?.status === 'active') {
          this.raceSecondsLeft = Math.max(
            0,
            Math.ceil((new Date(this.activeGame.ends_at).getTime() - Date.now()) / 1000),
          );
          if (!this.flushTimer) this.flushTimer = setInterval(() => this.flushTaps(), 500);
        }
      }).catch(() => undefined);
  }

  private flushTaps(): void {
    if (!this.activeGame?.id || this.unsentTaps <= 0) return;
    const count = this.unsentTaps;
    this.unsentTaps = 0;
    this.request.createRequestPost('profile-games', { count }, { id: this.activeGame.id }, 'tap')
      .catch(() => this.unsentTaps += count);
  }

  ngOnDestroy(): void {
    this.flushTaps();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.flushTimer) clearInterval(this.flushTimer);
  }
}
